import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@env';
import type {
  FeatureTier,
  PlanTier,
  PlanType,
  UserPlanId,
} from '@shared/models/subscription';
import type { Observable } from 'rxjs';

export interface SubscriptionStatus {
  canCreateOrg: boolean;
  subscriptionActive: boolean;
  subscriptionCancelledAtPeriodEnd: boolean;
  subscriptionCurrentPeriodEnd: string | null;
  subscriptionPlanType: PlanType;
  subscriptionPlanTier?: PlanTier | null;
  subscriptionPlan?: PlanTier | null;
  // SUB-004 — Core + add-ons model. `subscriptionFeatureTier` is always 'core'
  // now (kept for the DB column / status payload during the transition).
  subscriptionFeatureTier?: FeatureTier | null;
  // SUB-004 — POS is unlocked iff the subscription is active and flowUnits >= 1.
  hasPos?: boolean;
  subscriptionAmount?: number | null; // FULL recurring total in cents (base + all add-ons)
  subscriptionCurrency?: string | null;
  // SUB-004 — itemized breakdown so the billing card can show "Core €70 + AI+ ×2
  // + Flow ×2". One entry per Stripe subscription item; `label` is a stable key
  // the FE translates (never the Stripe product name). `unitAmount` in cents.
  subscriptionLineItems?: Array<{
    label: 'plan' | 'extra_till' | 'flow' | 'aiplus';
    unitAmount: number;
    quantity: number;
  }>;
  trialEndsAt?: string | null;
  trialExpired?: boolean;
  // SUB-004 — the owner's POS Kasse entitlement = number of Flow units. Each
  // Flow unit = 1 Kasse; >=1 enables POS. (tillLimit === flowUnits.)
  tillLimit?: number;
  // SUB-004 — number of Flow add-on units (alias of tillLimit, server-provided).
  flowUnits?: number;
  // SUB-004 — number of AI+ add-on units; each grants 1,000 tokens/month.
  aiUnits?: number;
  // SUB-004 — true iff aiUnits >= 1.
  withAi?: boolean;
  // SUB-006 — the highest active-Kasse count across the owner's orgs. The
  // binding floor for lowering Flow: each org is gated independently against
  // the owner's Flow limit, so Flow can't drop below the busiest org's count
  // without stranding active tills. 0 when no tills are running.
  activeTillCount?: number;
}

export interface PaymentRequirement {
  needsPayment: boolean;
  reason: string;
  canCreateOrg: boolean;
  trialExpired: boolean;
  trialDaysLeft: number;
  trialEndsAt: string | null;
  hasActiveSubscription: boolean;
  subscriptionExpired: boolean;
  subscriptionEndsAt: string | null;
  isOwner: boolean;
}

export interface CreateCheckoutSessionRequest {
  plan: UserPlanId;
  successUrl?: string;
  cancelUrl?: string;
  // SUB-004 — number of Flow add-on units (1 unit = 1 Kasse; >=1 enables POS).
  // 0..N, unbounded; default 0 (omitted = no Flow line item).
  flowUnits?: number;
  // SUB-004 — number of AI+ add-on units (each grants 1,000 tokens/month).
  // 0..N, unbounded; default 0 (omitted = no AI+ line item).
  aiUnits?: number;
}

export interface CreateCheckoutSessionResponse {
  url: string;
  sessionId: string;
}

// SUB-004 — result of changing an add-on (Flow or AI+) quantity on the live
// subscription. `quantity` echoes the new unit count; `tillLimit` reflects the
// Flow entitlement after the change.
export interface ChangeAddonResponse {
  ok: boolean;
  quantity: number;
  tillLimit: number;
  action: 'noop' | 'create' | 'update' | 'delete';
}

export interface DebugInfoResponse {
  customer?: {
    id: string;
    email: string;
    created: number;
  };
  subscriptions?: Array<{
    id: string;
    status: string;
    trialStart: number | null;
    trialEnd: number | null;
  }>;
}

export interface CancelSubscriptionResponse {
  message: string;
  cancelledSubscriptions: Array<{
    id: string;
    status: string;
    cancelAtPeriodEnd: boolean;
    currentPeriodEnd: number;
  }>;
  scheduledDate: string;
}

export interface CreatePortalSessionRequest {
  returnUrl?: string;
}

export interface CreatePortalSessionResponse {
  url: string;
  sessionId: string;
}

export interface StaffLimitError {
  error: string;
  message: string;
  details: {
    currentStaffCount: number;
    newPlanLimit: number;
    staffToRemove: number;
  };
}

@Injectable({
  providedIn: 'root',
})
export class SubscriptionApi {
  private http = inject(HttpClient);
  private baseUrl = environment.apiUrl;

  /**
   * Get the current subscription status for the authenticated user
   */
  getStatus(): Observable<SubscriptionStatus> {
    return this.http.get<SubscriptionStatus>(
      `${this.baseUrl}/subscriptions/status`,
    );
  }

  // New method: Check if user needs payment (for proactive checking)
  checkPaymentRequirement(): Observable<PaymentRequirement> {
    return this.http.get<PaymentRequirement>(
      `${this.baseUrl}/subscriptions/needs-payment`,
    );
  }

  /**
   * Create a new Stripe checkout session for subscription
   */
  createCheckoutSession(
    request: CreateCheckoutSessionRequest,
  ): Observable<CreateCheckoutSessionResponse> {
    return this.http.post<CreateCheckoutSessionResponse>(
      `${this.baseUrl}/subscriptions/create-checkout-session`,
      request,
    );
  }

  /**
   * SUB-004 — change the Flow add-on quantity on the EXISTING active
   * subscription. `quantity` is the number of Flow units (= Kasse; 0 removes
   * the add-on and locks POS). Prorated and invoiced immediately on increase;
   * the resulting webhook re-syncs `till_limit = flowUnits` so provisioning up
   * to the new count is permitted.
   */
  changeFlow(quantity: number): Observable<ChangeAddonResponse> {
    return this.http.post<ChangeAddonResponse>(
      `${this.baseUrl}/subscriptions/flow-addon`,
      { quantity },
    );
  }

  /**
   * SUB-004 — change the AI+ add-on quantity on the EXISTING active
   * subscription. `quantity` is the number of AI+ units; each grants 1,000
   * tokens/month. Prorated on increase; the webhook re-syncs the monthly token
   * allocation (aiUnits × 1,000) and `with_ai = aiUnits >= 1`.
   */
  changeAiAddon(quantity: number): Observable<ChangeAddonResponse> {
    return this.http.post<ChangeAddonResponse>(
      `${this.baseUrl}/subscriptions/ai-addon`,
      { quantity },
    );
  }

  /**
   * Get debug information about the user's Stripe customer and subscriptions
   * (useful for development and support)
   */
  getDebugInfo(): Observable<DebugInfoResponse> {
    return this.http.get<DebugInfoResponse>(
      `${this.baseUrl}/subscriptions/debug`,
    );
  }

  /**
   * Cancel the user's active subscription
   */
  cancelSubscription(): Observable<CancelSubscriptionResponse> {
    return this.http.post<CancelSubscriptionResponse>(
      `${this.baseUrl}/subscriptions/cancel`,
      {},
    );
  }

  /**
   * Create a guest checkout session (no auth required — used by quiz flow)
   */
  createGuestCheckoutSession(request: {
    plan: string;
    locale: string;
    wantWebsite: boolean;
    // SUB-005 — add-on units carried from the quiz recommendation.
    flowUnits?: number;
    aiUnits?: number;
  }): Observable<CreateCheckoutSessionResponse> {
    return this.http.post<CreateCheckoutSessionResponse>(
      `${this.baseUrl}/subscriptions/create-guest-checkout`,
      request,
    );
  }

  /**
   * Create a Stripe customer portal session for billing management
   */
  createPortalSession(
    request: CreatePortalSessionRequest,
  ): Observable<CreatePortalSessionResponse> {
    return this.http.post<CreatePortalSessionResponse>(
      `${this.baseUrl}/subscriptions/create-portal-session`,
      request,
    );
  }
}
